import React from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import Loadable from 'react-loadable';
import LinearProgress from '@material-ui/core/LinearProgress';
import _ from 'lodash';
import { RouteProps } from 'react-router';

import HasFeatures from 'retired/HasFeatures';
import Authenticated from 'retired/Authenticated';
import { Features } from 'utils/features';
import { Privileges } from 'utils/privileges';
import {
  CORPORATE_DONATION_URL_PATH,
  VOLUNTEER_REWARD_BUDGET_ALLOCATION,
  VOLUNTEER_REWARD_TYPE_NAME,
  VOLUNTEER_REWARD_URL_PATH,
} from 'entities/budgetAllocation/constants';
import AddressChangeRequestPage from 'containers/AddressChangeRequestPage';

const errorLoading = (err: any) => {
  console.error('Dynamic page loading failed', err); // eslint-disable-line no-console
};

/* eslint-disable react/prop-types */
const ProtectedRoute = ({
  component: PureComponent,
  hasFeatures,
  hasPrivileges,
  hasFeaturesRequireAll,
  ...rest
}: Route['props'] & {
  component: any;
  hasFeatures?: any;
  hasPrivileges?: any;
  hasFeaturesRequireAll?: any;
}) => (
  <Authenticated
    render={(isAuthenticated: boolean) => (
      <Route
        exact
        {...rest}
        render={(props) =>
          isAuthenticated ? (
            <HasFeatures
              component={PureComponent}
              hasFeatures={hasFeatures}
              hasPrivileges={hasPrivileges}
              requireAll={hasFeaturesRequireAll}
              ignoreFeatureFlag={(props.location.state as any)?.ignoreFeatureFlag}
              {...props}
            />
          ) : (
            <Redirect
              to={`/login?d=${props.location.pathname}${
                props.location.search ? `${encodeURIComponent(props.location.search)}` : ''
              }`}
            />
          )
        }
      />
    )}
  />
);
/* eslint-disable react/prop-types */
const UnauthenticatedOnlyRoute = ({
  component: PureComponent,
  redirectPath = '/', // default path to redirect authenticated users
  ...rest
}) => (
  <Authenticated
    render={(isAuthenticated: boolean) => (
      <Route
        {...rest}
        render={(props) =>
          !isAuthenticated ? <PureComponent {...props} /> : <Redirect to={redirectPath} />
        }
      />
    )}
  />
);
/* eslint-enable react/prop-types */
const createLoadable = (loader: Promise<any>) =>
  Loadable({
    loader: () => loader.then((module) => module.default || module).catch(errorLoading),
    loading: () => <LinearProgress />,
  });

const LoginComponent = createLoadable(import('containers/LoginPage'));
const SingleSignOnCallbackComponent = createLoadable(import('containers/SingleSignOnCallback'));

const LoginRoute = (routeProps: Partial<RouteProps>) => (
  <Authenticated
    render={(isAuthenticated) => {
      const params = _.fromPairs(
        _.compact(
          _.map(
            window.location.search.slice(1).split('&'),
            (parameter) => parameter && parameter.split('=')
          )
        )
      );
      return (
        <Route
          exact
          {...routeProps}
          render={(props) =>
            isAuthenticated ? (
              <Redirect to={(params.d && decodeURIComponent(params.d)) || '/'} />
            ) : (
              <LoginComponent {...props} />
            )
          }
        />
      );
    }}
  />
);

const LandingPageRoute = createLoadable(import('containers/LandingPage'));
const ForgotPasswordRoute = createLoadable(import('containers/ForgotPassword'));
const ResetPasswordRoute = createLoadable(import('containers/ResetPassword'));
const RegisterPageRoute = createLoadable(import('containers/RegisterPage'));
const RegisterVerifyPageRoute = createLoadable(import('containers/RegisterVerifyPage'));
const ActionAddEditPageRoute = createLoadable(import('containers/ActionPage'));
const ActionTemplateAddEditPageRoute = createLoadable(import('containers/ActionTemplatePage'));
const ActionsLibraryPageRoute = createLoadable(import('containers/ActionsLibraryPage'));
const ActionTemplatePreviewPageRoute = createLoadable(
  import('containers/ActionsLibraryPage/ActionTemplatePreview')
);
const CampaignsAddEditPageRoute = createLoadable(import('containers/CampaignPage'));
const CampaignsPageRoute = createLoadable(import('containers/CampaignsPage'));
const ChallengesPageRoute = createLoadable(import('containers/ChallengesPage'));
const ChallengePageRoute = createLoadable(import('containers/ChallengePage'));
const ChallengeFormPageRoute = createLoadable(
  import('containers/ChallengeFormPage/ChallengeFormPage')
);
const CausesPageRoute = createLoadable(import('containers/CausesPage'));
const TeamsCommunitiesPageRoute = createLoadable(import('containers/TeamsCommunitiesPage'));
const CommunityPageRoute = createLoadable(import('containers/CommunityPage'));
const CommunityAddEditPageRoute = createLoadable(import('containers/CommunityFormPage'));
const DeedPageRoute = createLoadable(import('containers/DeedPage'));
const DeedsPageRoute = createLoadable(import('containers/DeedsPage'));
const DeedFormPageRoute = createLoadable(import('containers/DeedFormPage'));
const DeedVolunteerMatchImport = createLoadable(import('containers/DeedVolunteerMatchImport'));
const OpportunitiesPageRoute = createLoadable(import('containers/OpportunitiesPage'));
const ApproveEmployeeDeeds = createLoadable(import('containers/ApproveEmployeeDeeds'));
const OrganizationPageRoute = createLoadable(import('containers/OrganizationPage'));
const FeaturesPageRoute = createLoadable(import('containers/FeaturesPage'));
const AppSettingsPageRoute = createLoadable(import('containers/AppSettingsPage'));
const AutoSourcingRoute = createLoadable(import('containers/AutoSourcingPage'));
const NonprofitsImportRoute = createLoadable(import('containers/NonprofitsImportPage'));
const PaymentsRoute = createLoadable(import('containers/PaymentsRoute'));
const VerificationRoute = createLoadable(import('containers/VerificationRoute'));
const SFTPConnectionFormPageRoute = createLoadable(import('containers/SFTPConnectionFormPage'));
const OrganizationAuthorizeStripePageRoute = createLoadable(
  import('containers/OrganizationAuthorizeStripePage')
);
const OrganizationStripeIntroPageRoute = createLoadable(
  import('containers/OrganizationStripeIntroPage')
);
const OrganizationPayPalIntroPage = createLoadable(
  import('containers/OrganizationPayPalIntroPage')
);
const NonprofitNominationsRoute = createLoadable(import('containers/NonprofitNominationsPage'));
const UserPageRoute = createLoadable(import('containers/UserPage'));
const LocationsListPageRoute = createLoadable(import('containers/LocationsListPage'));
const LocationFormPageRoute = createLoadable(import('containers/LocationFormPage'));
const OrganizersListPageRoute = createLoadable(import('containers/OrganizersListPage'));
const OrganizerFormPageRoute = createLoadable(import('containers/OrganizerFormPage'));
const VolunteersListPageRoute = createLoadable(import('containers/VolunteersListPage'));
const VolunteerPageRoute = createLoadable(import('containers/VolunteerPage'));
const VolunteerFormPageRoute = createLoadable(import('containers/VolunteerFormPage'));
const CreateVolunteerFormPageRoute = createLoadable(import('containers/CreateEmployeeFormPage'));
const FormsPageRoute = createLoadable(import('containers/FormsPage'));
const FormPageRoute = createLoadable(import('containers/FormPage'));
const FormFormPageRoute = createLoadable(import('containers/FormFormPage'));
const DonationsListPageRoute = createLoadable(import('containers/DonationsListPage'));
const DonationPageRoute = createLoadable(import('containers/DonationPage'));
const DonationSchedulePageRoute = createLoadable(import('containers/DonationSchedulePage'));
const BudgetFormPageRoute = createLoadable(import('containers/BudgetFormPage'));
const CauseFormPageRoute = createLoadable(import('containers/CauseFormPage'));
const DonationMatchingRulesFormPageRoute = createLoadable(
  import('containers/DonationMatchingRulesFormPage')
);
const DonationCreditsRulesFormPageRoute = createLoadable(
  import('containers/DonationCreditsRulesFormPage')
);
const BudgetAllocationFormPageRoute = createLoadable(import('containers/BudgetAllocationFormPage'));
const ExternalDonationRequestsRoute = createLoadable(import('containers/ExternalDonationRequests'));
const VolunteerTimesListPageRoute = createLoadable(import('containers/VolunteerTimesListPage'));
const VolunteerRewardsListPageRoute = createLoadable(import('containers/VolunteerRewardsListPage'));
const DonationRulesListPageRoute = createLoadable(import('containers/DonationRulesListPage'));
const OrganizationsListPageRoute = createLoadable(import('containers/OrganizationsListPage'));
const OrganizationDetailsPageRoute = createLoadable(import('containers/OrganizationDetailsPage'));
const PartnerRequestsRoute = createLoadable(import('containers/PartnerRequests'));
const ReportsPageRoute = createLoadable(import('containers/ReportsPage'));
const SigmaReportPage = createLoadable(import('containers/SigmaReportPage'));
const ReportsDetailsPageRoute = createLoadable(import('containers/ReportsDetailsPageRoute'));
const NotFoundPageRoute = createLoadable(import('containers/NotFoundPage'));
const GivingAccountRoute = createLoadable(import('containers/GivingAccountPage'));
const GivingAccountsRoute = createLoadable(import('containers/GivingAccountsPage'));
const DonationsSeedRoute = createLoadable(import('containers/DonationsSeedPage'));
const ReceiptDetailsPageRoute = createLoadable(
  import('containers/DonationsSeedPage/ReceiptDetailsPage')
);
const NotificationSettingsRoute = createLoadable(import('containers/NotificationSettingsPage'));
const ProductboardRoute = createLoadable(import('containers/ProductboardPage'));
const ChaptersRoute = createLoadable(import('containers/ChaptersPage'));
const ChapterRequestRoute = createLoadable(import('containers/ChapterRequestPage'));
const OnboardingTrackerRoute = createLoadable(import('containers/OnboardingTracker'));
const ImpactAiPageRoute = createLoadable(import('containers/BackOfficeImpactAiPresets'));
const CorporateDonationLogFormPageRoute = createLoadable(
  import('containers/CorporateDonationLogFormPage')
);
const CorporateDonationDonateFormPageRoute = createLoadable(
  import('containers/CorporateDonationDonateFormPage')
);
const PayrollPageRoute = createLoadable(import('containers/PayrollGenericPage'));
const PayrollRunPage = createLoadable(import('containers/PayrollFlatFilePage/PayrollRun'));
const SlackInstallRedirectRoute = createLoadable(import('containers/SlackInstallRedirectPage'));

const EditAffiliationRoute = createLoadable(
  import('containers/AffiliationsFormPage/EditAffiliation')
);
const AddAffiliationRoute = createLoadable(
  import('containers/AffiliationsFormPage/AddAffiliation')
);

const DashboardPage = createLoadable(import('containers/DashboardPage'));
const SigmaShadowDashboardPage = createLoadable(import('containers/SigmaShadowDashboardPage'));
const DashboardsListPage = createLoadable(import('containers/DashboardsListPage'));

const FeesPlaygroundPage = createLoadable(import('containers/FeesPlaygroundPage'));
const OnboardingPlaygroundPage = createLoadable(import('containers/OnboardingPlaygroundPage'));
const NameChangeRequestPage = createLoadable(import('containers/NameChangeRequestPage'));
const VerificationRequestPage = createLoadable(import('containers/VerificationRequestPage'));
const CreateInvoicePage = createLoadable(import('containers/CreateInvoicePage'));
const InvoicesPage = createLoadable(import('containers/InvoicesPage'));
const ExternalMatchRequestRulesPage = createLoadable(
  import('containers/ExternalMatchRequestRules/ExternalMatchRequestRulesList')
);
const ExternalMatchRequestRuleEditPage = createLoadable(
  import('containers/ExternalMatchRequestRules/ExternalMatchRequestRule')
);
const ExternalMatchRequestRulesWrapper = createLoadable(
  import('containers/ExternalMatchRequestRules/ExternalMatchRequestRulesWrapper')
);

const Routes = () => (
  <Switch>
    <ProtectedRoute exact path="/" component={LandingPageRoute} />

    <ProtectedRoute exact path="/fees-playground" component={FeesPlaygroundPage} />
    <ProtectedRoute exact path="/onboarding-playground" component={OnboardingPlaygroundPage} />

    <ProtectedRoute exact path="/dashboards" component={DashboardsListPage} />
    <ProtectedRoute exact path="/dashboards/shadow" component={SigmaShadowDashboardPage} />
    <ProtectedRoute exact path="/dashboards/:type?" component={DashboardPage} />
    <Redirect from="/dashboard/:type?" to="/dashboards/:type?" />

    <Route exact path="/login/sso/callback" component={SingleSignOnCallbackComponent} />
    <LoginRoute exact path="/login/sso/:companyId?" />
    <UnauthenticatedOnlyRoute
      exact
      path="/login/(reset|password)/:token?"
      component={ResetPasswordRoute}
    />
    <LoginRoute path="/login" />

    <UnauthenticatedOnlyRoute exact path="/forgot" component={ForgotPasswordRoute} />

    <UnauthenticatedOnlyRoute exact path="/register/:partnerToken?" component={RegisterPageRoute} />

    <UnauthenticatedOnlyRoute exact path="/verify/:token?" component={RegisterVerifyPageRoute} />
    <ProtectedRoute exact path="/actions/new" component={ActionAddEditPageRoute} />
    <ProtectedRoute exact path="/actions/edit/:id" component={ActionAddEditPageRoute} />
    <ProtectedRoute exact path="/actions/templates/browse" component={ActionsLibraryPageRoute} />
    <ProtectedRoute exact path="/actions/template/new" component={ActionTemplateAddEditPageRoute} />
    <ProtectedRoute
      exact
      path="/actions/template/edit/:id"
      component={ActionTemplateAddEditPageRoute}
    />
    <ProtectedRoute exact path="/actions/template/:id" component={ActionTemplatePreviewPageRoute} />
    <ProtectedRoute exact path="/campaigns/new" component={CampaignsAddEditPageRoute} />
    <ProtectedRoute exact path="/campaigns/edit/:id" component={CampaignsAddEditPageRoute} />
    <Redirect exact from="/campaigns" to="/campaigns/active" />
    <ProtectedRoute exact path="/campaigns/:type?" component={CampaignsPageRoute} />
    <ProtectedRoute exact path="/challenges" component={ChallengesPageRoute} />
    <ProtectedRoute exact path="/challenges/new" component={ChallengeFormPageRoute} />
    <ProtectedRoute exact path="/challenges/:id/edit" component={ChallengeFormPageRoute} />
    <ProtectedRoute exact path="/challenges/:id" component={ChallengePageRoute} />
    <ProtectedRoute exact path="/causes" component={CausesPageRoute} />
    <ProtectedRoute exact path="/communities" component={TeamsCommunitiesPageRoute} />
    <ProtectedRoute exact path="/communities/new" component={CommunityAddEditPageRoute} />
    <ProtectedRoute exact path="/communities/:id" component={CommunityPageRoute} />
    <ProtectedRoute exact path="/communities/edit/:id" component={CommunityAddEditPageRoute} />
    <ProtectedRoute exact path="/teams" component={TeamsCommunitiesPageRoute} />
    <ProtectedRoute exact path="/teams/new" component={CommunityAddEditPageRoute} />
    <ProtectedRoute exact path="/teams/:id" component={CommunityPageRoute} />
    <ProtectedRoute exact path="/teams/edit/:id" component={CommunityAddEditPageRoute} />

    <Redirect exact from="/deeds" to="/deeds/volunteer" />
    <ProtectedRoute exact path="/deeds/volunteer-match" component={DeedVolunteerMatchImport} />
    <ProtectedRoute exact path="/deeds/browse" component={OpportunitiesPageRoute} />
    <ProtectedRoute exact path="/deeds/submitted" component={ApproveEmployeeDeeds} />
    <ProtectedRoute exact path="/deeds/:type/:id" component={DeedPageRoute} />
    <ProtectedRoute exact path="/deeds/:type" component={DeedsPageRoute} />
    <ProtectedRoute exact path="/deeds/:type?/edit/:id" component={DeedFormPageRoute} />
    <ProtectedRoute exact path="/organization" component={OrganizationPageRoute} />
    <ProtectedRoute exact path="/features" component={FeaturesPageRoute} />
    <Redirect exact from="/appSettings" to="/app-settings" />
    <ProtectedRoute
      exact
      path="/payments"
      hasPrivileges={[Privileges.ORGANIZATION_MANAGE_SETTINGS]}
      component={PaymentsRoute}
    />
    <ProtectedRoute
      exact
      path="/verification"
      hasPrivileges={[Privileges.ORGANIZATION_SELF_VERIFY_OWNERSHIP]}
      component={VerificationRoute}
    />
    <ProtectedRoute exact path="/app-settings" component={AppSettingsPageRoute} />
    <ProtectedRoute exact path="/deed-sourcing" component={AutoSourcingRoute} />
    <ProtectedRoute exact path="/nonprofits-import" component={NonprofitsImportRoute} />
    <ProtectedRoute exact path="/sftp-connection" component={SFTPConnectionFormPageRoute} />
    <Redirect
      exact
      from="/(organizations|companies)/partners"
      to="/(organizations|companies)/partners/accepted"
    />
    <ProtectedRoute
      exact
      path="/(organizations|companies)/partners/:partnerStatus(pending|accepted|rejected)"
      component={PartnerRequestsRoute}
      hasPrivileges={[Privileges.PARTNER_VIEW]}
    />
    <Redirect exact from="/companies" to="/companies/partners" />
    <ProtectedRoute exact path="/companies/:id" component={OrganizationDetailsPageRoute} />
    <ProtectedRoute exact path="/organizations" component={OrganizationsListPageRoute} />
    <ProtectedRoute exact path="/organizations/:id" component={OrganizationDetailsPageRoute} />
    <Redirect exact from="/organization/authorizeStripe" to="/organization/stripe/authorization" />
    <ProtectedRoute
      exact
      path="/organization/stripe/authorization"
      component={OrganizationAuthorizeStripePageRoute}
    />
    <Redirect exact from="/organization/stripeIntro" to="/organization/stripe/connect" />
    <ProtectedRoute
      exact
      path="/organization/stripe/connect"
      component={OrganizationStripeIntroPageRoute}
    />
    <ProtectedRoute
      exact
      path="/organization/payPalIntro"
      component={OrganizationPayPalIntroPage}
    />
    <ProtectedRoute exact path="/nonprofit-nominations" component={NonprofitNominationsRoute} />
    <ProtectedRoute exact path="/user" component={UserPageRoute} />
    <ProtectedRoute exact path="/locations" component={LocationsListPageRoute} />
    <ProtectedRoute exact path="/locations/:id" component={LocationFormPageRoute} />
    <ProtectedRoute exact path="/organizers" component={OrganizersListPageRoute} />
    <ProtectedRoute exact path="/organizers/:id" component={OrganizerFormPageRoute} />
    <ProtectedRoute exact path="/volunteers" component={VolunteersListPageRoute} />
    <ProtectedRoute exact path="/volunteers/new" component={CreateVolunteerFormPageRoute} />
    <ProtectedRoute exact path="/volunteers/:id" component={VolunteerPageRoute} />
    <ProtectedRoute exact path="/volunteers/:id/edit" component={VolunteerFormPageRoute} />
    <ProtectedRoute
      exact
      path="/volunteers/:userId/affiliations/new"
      component={AddAffiliationRoute}
      hasFeatures={[Features.EMPLOYEE_AFFILIATIONS]}
      hasPrivileges={[Privileges.EMPLOYEE_AFFILIATIONS_MANAGE]}
    />
    <ProtectedRoute
      exact
      path="/volunteers/:userId/affiliations/edit/:affiliationId"
      component={EditAffiliationRoute}
      hasFeatures={[Features.EMPLOYEE_AFFILIATIONS]}
      hasPrivileges={[Privileges.EMPLOYEE_AFFILIATIONS_MANAGE]}
    />
    <Redirect exact path="/donations" to="/donations/donors" />
    <ProtectedRoute
      exact
      path="/donations/:type(donors|corporate|company-donation-on-user-behalf)?"
      component={DonationsListPageRoute}
    />
    <ProtectedRoute
      exact
      path="/donations/corporate/donate"
      component={CorporateDonationDonateFormPageRoute}
    />
    <ProtectedRoute
      exact
      path="/donations/corporate/log"
      component={CorporateDonationLogFormPageRoute}
    />
    <Redirect exact from="/donations/external" to="/donations/external/pending" />
    <ProtectedRoute
      exact
      path="/donations/external/:matchingRequestStatus(pending|approved|denied)"
      component={ExternalDonationRequestsRoute}
    />
    <Route path="/donations/external-match-request-rules">
      <ExternalMatchRequestRulesWrapper>
        <Switch>
          <ProtectedRoute
            exact
            path="/donations/external-match-request-rules"
            component={() => <ExternalMatchRequestRulesPage />}
            hasPrivileges={[Privileges.EXTERNAL_MATCH_REQUEST_RULES_MANAGE]}
            hasFeatures={[Features.EXTERNAL_MATCHING_RULES]}
          />
          <ProtectedRoute
            exact
            path="/donations/external-match-request-rules/new"
            component={() => <ExternalMatchRequestRuleEditPage />}
            hasPrivileges={[Privileges.EXTERNAL_MATCH_REQUEST_RULES_MANAGE]}
            hasFeatures={[Features.EXTERNAL_MATCHING_RULES]}
          />
          <ProtectedRoute
            exact
            path="/donations/external-match-request-rules/:id"
            component={() => <ExternalMatchRequestRuleEditPage />}
            hasPrivileges={[Privileges.EXTERNAL_MATCH_REQUEST_RULES_MANAGE]}
            hasFeatures={[Features.EXTERNAL_MATCHING_RULES]}
          />
        </Switch>
      </ExternalMatchRequestRulesWrapper>
    </Route>

    <ProtectedRoute exact path="/donations/rules" component={DonationRulesListPageRoute} />
    <ProtectedRoute
      exact
      path="/donations/matching-rule/:id/:budgetId?"
      component={DonationMatchingRulesFormPageRoute}
    />
    <ProtectedRoute
      exact
      path="/donations/credit-rule/:id/:budgetId?"
      component={DonationCreditsRulesFormPageRoute}
    />
    <ProtectedRoute
      exact
      path={`/donations/${CORPORATE_DONATION_URL_PATH}/:id/:budgetId?`}
      component={BudgetAllocationFormPageRoute}
    />
    <ProtectedRoute
      exact
      path={`/donations/${VOLUNTEER_REWARD_URL_PATH}/:id/:budgetId?`}
      component={(props) => (
        <BudgetAllocationFormPageRoute
          {...props}
          budgetAllocationTypeName={VOLUNTEER_REWARD_TYPE_NAME}
          budgetType={VOLUNTEER_REWARD_BUDGET_ALLOCATION}
          extraMatchingGrantsFields
        ></BudgetAllocationFormPageRoute>
      )}
    />
    <ProtectedRoute exact path="/donations/payroll" component={PayrollPageRoute} />
    <ProtectedRoute
      exact
      path="/donations/payroll/run/:id/:paygroupId"
      component={PayrollRunPage}
    />
    <ProtectedRoute exact path="/donations/budgets/:id" component={BudgetFormPageRoute} />
    <ProtectedRoute exact path="/donations/ledger/:id" component={GivingAccountRoute} />
    <ProtectedRoute exact path="/donations/ledger" component={GivingAccountsRoute} />
    <ProtectedRoute exact path="/donations/credit-seeding" component={DonationsSeedRoute} />
    <ProtectedRoute
      exact
      path="/donations/credit-seeding/:id"
      component={ReceiptDetailsPageRoute}
    />
    <ProtectedRoute exact path="/donations/:id" component={DonationPageRoute} />
    <ProtectedRoute exact path="/donations/schedules/:id" component={DonationSchedulePageRoute} />
    <ProtectedRoute exact path="/(forms|surveys)" component={FormsPageRoute} />
    <ProtectedRoute exact path="/(forms|surveys)/:id" component={FormPageRoute} />
    <ProtectedRoute exact path="/(forms|surveys)/edit/:id" component={FormFormPageRoute} />
    <ProtectedRoute exact path="/causes/edit/:id" component={CauseFormPageRoute} />
    <ProtectedRoute exact path="/volunteer-times" component={VolunteerTimesListPageRoute} />
    <ProtectedRoute exact path="/volunteer-rewards" component={VolunteerRewardsListPageRoute} />
    <ProtectedRoute exact path="/reports" component={ReportsPageRoute} />
    <ProtectedRoute exact path="/reports/:type" component={SigmaReportPage} />
    <ProtectedRoute exact path="/reports/:family/:type" component={ReportsDetailsPageRoute} />
    <ProtectedRoute
      exact
      path="/:type(grants)"
      component={SigmaReportPage}
      hasPrivileges={[Privileges.GRANTS_MANAGE]}
      hasFeatures={[Features.GRANTS]}
    />
    <ProtectedRoute exact path="/notifications" component={NotificationSettingsRoute} />
    <ProtectedRoute exact path="/feedback" component={ProductboardRoute} />
    <Redirect exact from="/chapters" to="/chapters/accepted" />
    <ProtectedRoute
      exact
      path="/chapters/:type(accepted|pending|rejected)"
      component={ChaptersRoute}
      hasPrivileges={[Privileges.CHAPTERS_MANAGE]}
    />
    <ProtectedRoute
      exact
      path="/chapters/pending/:id"
      component={ChapterRequestRoute}
      hasPrivileges={[Privileges.CHAPTERS_MANAGE]}
    />
    <ProtectedRoute
      exact
      path="/onboarding"
      component={OnboardingTrackerRoute}
      hasPrivileges={[Privileges.ORGANIZATIONS_VIEW]}
    />
    <ProtectedRoute exact path="/name-change-request/:data" component={NameChangeRequestPage} />
    <ProtectedRoute
      exact
      path="/address-change-request/:data"
      component={AddressChangeRequestPage}
    />
    <ProtectedRoute exact path="/verification-request/:data" component={VerificationRequestPage} />

    <ProtectedRoute
      exact
      path="/invoices"
      component={InvoicesPage}
      hasPrivileges={[Privileges.RESTRICTED_INVOICING_VIEW]}
    />
    <ProtectedRoute
      exact
      path="/invoices/create"
      component={CreateInvoicePage}
      hasPrivileges={[Privileges.RESTRICTED_INVOICING_MANAGE]}
    />

    {/* Back Office Routes - Admin Only */}
    <ProtectedRoute exact path="/back-office" component={() => <div>Back Office Dashboard</div>} />
    <ProtectedRoute
      exact
      path="/back-office/impact-ai"
      component={ImpactAiPageRoute}
      hasPrivileges={[]}
    />

    <Route
      exact
      path="/thanks/slack/:status(success|failure)"
      component={SlackInstallRedirectRoute}
    />
    <Route exact path="*" component={NotFoundPageRoute} />
  </Switch>
);

export default Routes;
